import React, { useEffect, useRef, useState } from 'react';
import type {
  BasePatcherProps,
  WithPassedPatchableGroupProps
} from './PatchableGroup';
import { DEFAULT_DEBOUNCE_TIMEOUT } from './PatchableGroup';
import { NumericInput, NumericInputProps } from '@ianesthesie-kit/core';
import { useErrorNudgeWrapper } from '@ianesthesie-kit/motion';

export type NumericInputPatcherProps = BasePatcherProps &
  Omit<NumericInputProps, 'value' | 'onChange' | keyof BasePatcherProps> & {
    floatingPoint?: boolean;
    percentage?: boolean;
    // TODO move to kit
    getSuffixStyle?: (
      value?: string
    ) => NumericInputPatcherProps['suffixStyle'];
  };

const decimalToPercentage = (arg: string | number | null | undefined) => {
  if (!arg) {
    return arg;
  }

  const num = typeof arg === 'number' ? arg : +arg;
  if (Number.isNaN(num)) {
    throw new Error(`${arg} is not a number`);
  }

  const result = num * 100;
  // Round to avoid floating-point precision issues and remove trailing zeros
  const rounded = Math.round(result * 100) / 100;
  // Convert to string and back to number to remove trailing zeros
  return +rounded.toFixed(2).replace(/\.?0+$/, '');
};

const percentageToDecimal = (arg: string | number | null | undefined) => {
  if (!arg) {
    return arg;
  }

  const num = typeof arg === 'number' ? arg : +arg;
  if (Number.isNaN(num)) {
    throw new Error(`${arg} is not a number`);
  }

  return num / 100;
};

const NumericInputPatcherComponent = <T,>({
  /* eslint-disable @typescript-eslint/no-unused-vars */
  name,
  error,
  wrapper,
  patchable,
  loading,
  hideLoadingOverlay,
  floatingPoint,
  percentage,
  suffix,
  getSuffixStyle,
  sourceType,
  /* eslint-enable @typescript-eslint/no-unused-vars */
  ...restProps
}: WithPassedPatchableGroupProps<T, NumericInputPatcherProps>) => {
  const [instantValue, setInstantValue] = useState(
    patchable?.currentValue ?? ''
  );
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const keyboardInput = useRef(false);
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const val = patchable?.currentValue ?? '';
    setInstantValue(percentage ? decimalToPercentage(val) : val);
  }, [patchable?.currentValue, name, percentage]);

  if (!patchable) {
    throw new Error(
      'A Patcher component may be used as a child of a PatchableGroup'
    );
  }

  const equalsToCurrentValue = (val: number | undefined) => {
    if (typeof val !== 'number' && !val && !patchable.currentValue) {
      return true;
    }
    if (!percentage) {
      return patchable.currentValue === +(val ?? 0);
    }

    return patchable.currentValue === percentageToDecimal(val);
  };

  const { animate, NudgeWrapper } = useErrorNudgeWrapper();

  const doPatch = (v: number | undefined) => {
    if (patchable && !equalsToCurrentValue(v)) {
      const { onPatch } = patchable;
      const num = Number.isNaN(v) ? null : v;
      return onPatch({ [name]: percentage ? percentageToDecimal(num) : num });
    }

    return Promise.resolve();
  };
  const onChange = (v: string | undefined) => {
    setInstantValue(v ?? '');
    const newVal = +(v || Number.NaN);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    const min = restProps.min ?? -Infinity;
    const max = restProps.max ?? Infinity;
    if (Number.isNaN(newVal) || (newVal >= min && newVal <= max)) {
      if (keyboardInput.current) {
        timerRef.current = setTimeout(() => {
          doPatch(newVal).then(() => {
            setTimeout(() => ref?.current?.focus({ preventScroll: true }));
          });
        }, DEFAULT_DEBOUNCE_TIMEOUT);
      } else {
        doPatch(newVal);
      }
    }
  };

  const onBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    restProps?.onBlur?.(e);
    if (patchable.readOnly) {
      return;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const newVal = +(instantValue || Number.NaN);
    const min = restProps.min ?? -Infinity;
    const max = restProps.max ?? Infinity;
    if (!Number.isNaN(newVal) && (newVal < min || newVal > max)) {
      const resetVal = patchable.currentValue ?? '';
      animate().then(
        () =>
          (timerRef.current = setTimeout(() => {
            setInstantValue(
              percentage ? decimalToPercentage(resetVal) : resetVal
            );
          }, 250))
      );
      return;
    }

    doPatch(instantValue);
  };

  const setKeyboardInput = () => {
    keyboardInput.current = true;
    setTimeout(unsetKeyboardInput, DEFAULT_DEBOUNCE_TIMEOUT);
  };

  const unsetKeyboardInput = () => {
    keyboardInput.current = false;
  };

  return (
    <NudgeWrapper>
      <NumericInput
        fullWidth
        centered={false}
        {...restProps}
        showButtons={restProps.showButtons && !patchable?.readOnly}
        step={restProps.step ?? (floatingPoint ? 0.1 : undefined)}
        onChange={onChange}
        onBlur={onBlur}
        onInput={setKeyboardInput}
        onClick={unsetKeyboardInput}
        size="small"
        color="riskNone"
        value={instantValue === 0 ? '0' : instantValue}
        disabled={restProps.disabled || loading}
        InputProps={{
          inputRef: ref,
          ...restProps.InputProps,
          readOnly: patchable.readOnly
        }}
        suffix={suffix ?? (percentage ? '%' : suffix)}
        suffixStyle={{
          ...restProps.suffixStyle,
          ...getSuffixStyle?.(instantValue && `${instantValue}`),
          top: 2
        }}
      />
    </NudgeWrapper>
  );
};

export const NumericInputPatcher = (props: NumericInputPatcherProps) => (
  <NumericInputPatcherComponent {...props} />
);
